import React, { useState } from 'react';
import PageHero from './PageHero';
import {
  Trophy,
  Palette,
  Cpu,
  Globe,
  Heart,
  Sparkles,
  CheckCircle2
} from 'lucide-react';
import holisticImg from '../assets/WHY GMIS - HOLISTIC.png';

// ── Sports images
import footballTeamImg from '../assets/extracuricular/WhatsApp Image 2026-07-31 at 10.16.06.jpg';
import basketballTeamImg from '../assets/extracuricular/WhatsApp Image 2026-07-31 at 10.27.03.jpg';
import taekwondoImg from '../assets/extracuricular/dcf1ef3ed65f92ebb07f6b500602d10d.webp';
import futsalImg from '../assets/extracuricular/GMIS JAKARTA WEBSITE AMENDS.png';

// ── Arts / Music images
import orchestraImg from '../assets/extracuricular/WhatsApp Image 2026-10-01 at 20.47.20.jpg';
import bandImg from '../assets/extracuricular/WhatsApp Image 2026-10-05 at 09.31.04.jpg';
import danceImg from '../assets/extracuricular/WhatsApp Image 2026-10-06 at 09.12.47.jpg';
import indianDanceImg from '../assets/extracuricular/WhatsApp Image 2026-10-06 at 09.12.49.jpg';

// ── Leadership images
import gmisFortiusImg from '../assets/extracuricular/WhatsApp Image 2026-09-12 at 16.14.42 (1).jpg';
import wscWinnersImg from '../assets/extracuricular/3947209a-8536-4dbc-8ccc-2b82e21275c4.JPG';
import wscGlobalImg from '../assets/extracuricular/7c935a23-b547-4c41-9a26-26a08899e5d9.JPG';

export default function ExtracurricularActivitiesPage({ setPage, t }) {
  const [activeCategory, setActiveCategory] = useState('all');

  const navCategory = t?.nav?.[3]?.label || "School Life";
  const homeLabel = t?.nav?.[0]?.label || "Home";

  const categories = [
    {
      id: 'sports',
      title: 'Sports & Physical Activities',
      icon: <Trophy className="w-5 h-5 text-amber-600" />,
      badgeBg: 'bg-amber-500/10 text-amber-700',
      description: 'Our sports activities encourage students to stay active while developing teamwork, discipline, and a healthy sense of competition. Students have opportunities to participate in a range of individual and team-based activities, from badminton and swimming to football, basketball, volleyball, and martial arts.',
      images: [
        { src: futsalImg, alt: 'Futsal Match in Action' },
        { src: footballTeamImg, alt: 'GMIS Football Team' },
        { src: basketballTeamImg, alt: 'GMIS Basketball Team' },
        { src: taekwondoImg, alt: 'Taekwondo Practice' }
      ],
      activities: [
        'Badminton Club',
        'Basketball',
        'Cricket',
        'Cricket for Teachers & Parents',
        'Football',
        'Futsal Club',
        'Skating',
        'Swimming',
        'Table Tennis',
        'Taekwondo',
        'Volleyball',
        'Yoga for Teachers & Parents'
      ]
    },
    {
      id: 'arts',
      title: 'Arts, Music & Creative',
      icon: <Palette className="w-5 h-5 text-rose-600" />,
      badgeBg: 'bg-rose-500/10 text-rose-700',
      description: 'Creative activities provide students with a space to express themselves, explore their talents, and develop their artistic skills. From dance, music, and orchestra to arts, crafts, calligraphy, crochet, and creative writing, students can explore different forms of creative expression.',
      images: [
        { src: danceImg, alt: 'Dance Club Performance' },
        { src: orchestraImg, alt: 'Orchestra & Band Performance' },
        { src: bandImg, alt: 'Guitar Band Performance' },
        { src: indianDanceImg, alt: 'Cultural Dance Performance' }
      ],
      activities: [
        'Art & Craft Club',
        'Arts Club',
        'Calligraphy Club',
        'Crochet Club',
        'Dance',
        'Glee Club',
        'Orchestra Club',
        'Creative Writing & Zine Club'
      ]
    },
    {
      id: 'stem',
      title: 'STEM, Academic & Technology',
      icon: <Cpu className="w-5 h-5 text-sky-600" />,
      badgeBg: 'bg-sky-500/10 text-sky-700',
      description: 'These activities encourage students to explore ideas, solve problems, and apply what they learn in practical ways. Through STEM, robotics, engineering, mathematics, and technology-focused activities, students are encouraged to be curious, think critically, and develop their skills through hands-on learning.',
      images: [],
      activities: [
        'Engineering',
        'Mathematics Club',
        'Meta Minds Club',
        'Robotics',
        'STEM Club'
      ]
    },
    {
      id: 'leadership',
      title: 'Leadership, Global & Community',
      icon: <Globe className="w-5 h-5 text-indigo-600" />,
      badgeBg: 'bg-indigo-500/10 text-indigo-700',
      description: 'Students can take part in activities that develop leadership, communication, collaboration, and an understanding of the wider world. These clubs give students opportunities to engage in discussions, explore global issues, participate in youth-led initiatives, and contribute to the school and wider community.',
      images: [
        { src: wscGlobalImg, alt: 'GMIS World Scholars — WSC Global Round 2026', featured: true },
        { src: gmisFortiusImg, alt: 'GMIS Fortius Awards Ceremony' },
        { src: wscWinnersImg, alt: 'WSC Global Round Winners with Trophies' }
      ],
      activities: [
        'Generation Global Club',
        'GMUN Club',
        'Leo Club',
        'TEDxYouth Club',
        'TALKx Junior Club',
        'TALKx Youth Club',
        'WSC Club',
        'WSC Skittles Club'
      ]
    },
    {
      id: 'lifeskills',
      title: 'Life Skills & Well-being',
      icon: <Heart className="w-5 h-5 text-emerald-600" />,
      badgeBg: 'bg-emerald-500/10 text-emerald-700',
      description: 'Beyond academics, students are encouraged to develop practical skills and take care of their personal well-being. Activities such as baking, gardening, reading, daily life skills, and well-being provide opportunities to learn useful skills, build healthy habits, and enjoy learning in a relaxed setting.',
      images: [],
      activities: [
        'Baking Club',
        'Book Club',
        'Daily Life Skills',
        'Gardening Club',
        'Well-Being Club'
      ]
    }
  ];

  const displayedCategories = activeCategory === 'all'
    ? categories
    : categories.filter(c => c.id === activeCategory);

  return (
    <div className="w-full bg-[#f8f9fa] min-h-screen text-ink">
      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <PageHero
        title="Extracurricular Activities"
        eyebrow={navCategory}
        subtitle="Explore interests, develop new skills, and connect beyond the classroom."
        breadcrumbs={[homeLabel, navCategory, "Extracurricular Activities"]}
        bgImage={holisticImg}
      />

      {/* ── Main Content Container ────────────────────────────────────────── */}
      <div className=" mx-auto px-6 lg:px-[80px] py-16 lg:py-24 box-border">

        {/* ── Introduction Lead & Badge ── */}
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 mb-3 px-3.5 py-1 rounded-full border border-gold/30 bg-gold/10">
            <span className="text-gold text-[11px] font-bold tracking-[0.2em] uppercase">{navCategory}</span>
          </div>
          <h2 className="m-0 font-heading font-bold text-3xl md:text-4xl text-navy tracking-tight mb-4 uppercase">
            Extracurricular Activities
          </h2>
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <p className="text-[17px] md:text-[19px] text-navy font-medium leading-relaxed mb-6">
            At GMIS Jakarta, extracurricular activities give students the opportunity to explore their interests beyond the classroom. Through sports, creative pursuits, academic clubs, leadership programmes, and practical activities, students can develop new skills, build confidence, and connect with others while discovering what they enjoy.
          </p>

          {/* Metric Pill */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-white text-xs font-heading font-bold tracking-widest uppercase shadow-md">
            <Sparkles className="w-4 h-4 text-gold" />
            <span>Total: 38 unique activities</span>
          </div>
        </div>

        {/* ── Filter Tabs for Mobile & Desktop ── */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-14">
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all cursor-pointer ${activeCategory === 'all'
                ? 'bg-navy text-white shadow-md'
                : 'bg-white text-ink/70 border border-line hover:border-navy/40'
              }`}
          >
            All Activities (38)
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wide transition-all cursor-pointer ${activeCategory === cat.id
                  ? 'bg-navy text-white shadow-md'
                  : 'bg-white text-ink/70 border border-line hover:border-navy/40'
                }`}
            >
              {cat.title} ({cat.activities.length})
            </button>
          ))}
        </div>

        {/* ── Category Cards ── */}
        <div className="space-y-12">
          {displayedCategories.map((cat) => (
            <div
              key={cat.id}
              className="bg-white rounded-2xl p-7 lg:p-10 border border-line shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Category Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-paper flex items-center justify-center shrink-0 shadow-sm border border-line/60">
                    {cat.icon}
                  </div>
                  <div>
                    <h3 className="text-2xl font-heading font-bold text-navy m-0 uppercase">
                      {cat.title}
                    </h3>
                  </div>
                </div>
                <span className={`self-start md:self-auto text-xs font-bold px-3 py-1 rounded-full ${cat.badgeBg}`}>
                  {cat.activities.length} Activities
                </span>
              </div>

              {/* Description */}
              <p className="text-[16px] text-ink/80 leading-relaxed mb-8 pt-2">
                {cat.description}
              </p>

              {/* ── Big Images Gallery ── */}
              {cat.images && cat.images.length > 0 && (
                <div className="mb-8">
                  {cat.images.length === 4 ? (
                    /* 2x2 grid for 4 images (Sports and Arts): Big and spacious */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      {cat.images.map((img, i) => (
                        <div
                          key={i}
                          className="relative h-64 sm:h-72 md:h-80 w-full rounded-2xl overflow-hidden shadow-sm group bg-navy/5"
                        >
                          <img
                            src={img.src}
                            alt={img.alt}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                          <div className="absolute bottom-4 left-4 right-4">
                            <span className="inline-block text-white text-sm font-semibold bg-navy/70 backdrop-blur-sm px-3.5 py-1 rounded-lg shadow-sm">
                              {img.alt}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Leadership (3 images): 1 full-width featured hero + 2 large half-width below */
                    <div className="space-y-5">
                      {cat.images.filter(img => img.featured).map((img, i) => (
                        <div
                          key={i}
                          className="relative h-72 sm:h-80 md:h-96 w-full rounded-2xl overflow-hidden shadow-sm group bg-navy/5"
                        >
                          <img
                            src={img.src}
                            alt={img.alt}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                          <div className="absolute bottom-4 left-4 right-4">
                            <span className="inline-block text-white text-sm font-semibold bg-navy/70 backdrop-blur-sm px-3.5 py-1 rounded-lg shadow-sm">
                              {img.alt}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {cat.images.filter(img => !img.featured).map((img, i) => (
                          <div
                            key={i}
                            className="relative h-64 sm:h-72 md:h-80 w-full rounded-2xl overflow-hidden shadow-sm group bg-navy/5"
                          >
                            <img
                              src={img.src}
                              alt={img.alt}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />
                            <div className="absolute bottom-4 left-4 right-4">
                              <span className="inline-block text-white text-sm font-semibold bg-navy/70 backdrop-blur-sm px-3.5 py-1 rounded-lg shadow-sm">
                                {img.alt}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Activity Chips Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 pt-6 border-t border-line/60">
                {cat.activities.map((act, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2.5 p-3 rounded-xl bg-paper/60 border border-line/50 hover:bg-paper transition-colors"
                  >
                    <CheckCircle2 className="w-4 h-4 text-gold shrink-0" />
                    <span className="text-[14px] font-medium text-ink">
                      {act}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── Total Activities Bottom Callout Banner ── */}
        <div className="mt-16 w-full bg-navy rounded-3xl p-10 md:p-14 text-center text-white shadow-xl relative overflow-hidden">
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <h3 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl leading-snug max-w-2xl mx-auto tracking-tight mb-4 uppercase">
            Total: 38 unique activities
          </h3>
          <p className="text-white/80 text-[16px] max-w-xl mx-auto leading-relaxed mb-8">
            Giving students the opportunity to explore their interests beyond the classroom, develop skills, and build confidence.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => {
                if (setPage) setPage('contact');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="px-8 py-3.5 rounded-lg bg-gold text-navy font-heading font-bold text-xs uppercase tracking-widest hover:bg-gold/90 transition-all shadow-md cursor-pointer"
            >
              Contact Us
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
